import clsx from "clsx";

type PillSize = "tag" | "sm" | "lg";

// sm/lg are the design system's dire-chip (24 / 40 px, semibold label).
const sizeClassMapping: Record<
  PillSize,
  { pill: string; gap: string; text: string }
> = {
  // Stand-in for dire-tag (not built yet), used by the itemized bill filters.
  tag: { pill: "px-2 py-0.5", gap: "gap-1", text: "text-body-xs" },
  sm: {
    pill: "h-6 px-2 py-1",
    gap: "gap-1",
    text: "text-body-xs font-semibold leading-none",
  },
  lg: {
    pill: "h-10 px-3 py-2",
    gap: "gap-1",
    text: "text-body-lg font-semibold leading-none",
  },
};

interface PillProps {
  text: React.ReactNode;
  icon?: React.ReactNode;
  /** Which side of the text the icon sits on. Defaults to "leading". */
  iconPosition?: "leading" | "trailing";
  size?: PillSize;
  /**
   * Accessible name for pills whose visible text does not carry the meaning on
   * its own, e.g. a "+3" overflow chip.
   */
  ariaLabel?: string;
  className?: string;
  dataTestid?: string;
}

export const Pill: React.FC<PillProps> = ({
  text,
  icon,
  iconPosition = "leading",
  size = "sm",
  ariaLabel,
  className,
  dataTestid,
}) => {
  const sizeClasses = sizeClassMapping[size];

  return (
    // `span`, not `div`: chips sit inside paragraphs and other inline content.
    <span
      data-testid={dataTestid}
      className={clsx(
        "inline-flex shrink-0 items-center whitespace-nowrap rounded-full",
        sizeClasses.pill,
        sizeClasses.gap,
        className,
      )}
      // A plain `span` is not a naming-capable element, so `aria-label` alone
      // would be ignored — `role="img"` makes the chip announce as its label
      // instead of as the bare glyph.
      role={ariaLabel ? "img" : undefined}
      aria-label={ariaLabel}
    >
      <span className={clsx("flex items-center", sizeClasses.gap)}>
        {iconPosition === "leading" && icon}
        <span className={sizeClasses.text}>{text}</span>
        {iconPosition === "trailing" && icon}
      </span>
    </span>
  );
};
